import { DataSource, DataSourceCodeConfig, DataSourceIDEnum } from '@/models/dataSource';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { FC, useEffect } from 'react';
import { CREATE_DATA_SOURCE_CODE, CREATE_DATA_SOURCE_CODE_STEPS } from './constants';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { Loadable } from '@/components/loaders';
import { useHistory } from 'react-router-dom';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useDispatch } from 'react-redux';
import { RouteConstants } from '@/features/router/RouteConstants';
import { CheckInstallationStep, InstallationSuccessStep, SnippetPreviewStep, SourceDetailsStep, TrackWebsiteActivityStep } from './steps';
import { useAppSelector } from '@/reducers';

type CreateDataSourceCodeContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourceCodeContent: FC<CreateDataSourceCodeContentProps> = ({ dataSourceId, modalProps }) => {
  const dispatch = useDispatch();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(CREATE_DATA_SOURCE_CODE_STEPS);

  const history = useHistory();

  const { isFetching, data: sources } = useGetDataSourceTypesQuery();
  const dataSourceType = sources?.find((source) => source.id === dataSourceId);
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.createCampaign);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_CODE.SOURCE_DETAILS:
        return <SourceDetailsStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} dataSourceType={dataSourceType} />;

      case CREATE_DATA_SOURCE_CODE.TRACK_ACTIVITY:
        return <TrackWebsiteActivityStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CODE.INSTALL_SNIPPET:
        return <SnippetPreviewStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CODE.CHECK_INSTALLATION:
        return <CheckInstallationStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CODE.INSTALLATION_SUCCESS:
        return <InstallationSuccessStep onInit={completeCurrentStep} onNextStep={handleCloseAndContinue} onPrevStep={handleCloseOnly} isModal={!!modalProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      <Loadable isLoading={isFetching}>{stepTemplate()}</Loadable>
    </>
  );
};
